import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Truck, X } from "lucide-react";
import { fetchOrderShipping, syncOrderShipping, translateShippingSyncError } from "../../api/ShippingAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";

const STATE_LABELS = {
  PENDING: "قيد الانتظار",
  IN_TRANSIT: "قيد الشحن",
  OUT_FOR_DELIVERY: "خرج للتسليم",
  FAILED_ATTEMPT: "محاولة فاشلة",
  DELIVERED: "تم التسليم",
  RETURN_IN_PROGRESS: "جاري الإرجاع",
  RETURNED: "مرتجع",
  CANCELLED: "ملغي",
  PROBLEMATIC: "مشكلة",
};

const STATE_COLORS = {
  PENDING: "bg-slate-100 text-slate-700 border-slate-200",
  IN_TRANSIT: "bg-sky-50 text-sky-800 border-sky-200",
  OUT_FOR_DELIVERY: "bg-indigo-50 text-indigo-800 border-indigo-200",
  FAILED_ATTEMPT: "bg-amber-50 text-amber-800 border-amber-200",
  DELIVERED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  RETURN_IN_PROGRESS: "bg-orange-50 text-orange-800 border-orange-200",
  RETURNED: "bg-rose-50 text-rose-800 border-rose-200",
  CANCELLED: "bg-gray-100 text-gray-700 border-gray-300",
  PROBLEMATIC: "bg-red-50 text-red-800 border-red-200",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("ar-EG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const shippingStateLabel = (state) => STATE_LABELS[state] || state || "غير متزامن";

export const shippingStateClass = (state) =>
  STATE_COLORS[state] || "bg-slate-100 text-slate-700 border-slate-200";

export const ShippingTimeline = ({ orderId, compact = false }) => {
  const { authState } = useAuth();
  const canSync = canMutateOrders(authState.role);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [syncNotice, setSyncNotice] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["orderShipping", orderId],
    queryFn: () => fetchOrderShipping(orderId),
    enabled: !!orderId,
  });

  const logs = useMemo(
    () => [...(data?.logs || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [data]
  );

  const handleSync = async () => {
    if (!canSync || !orderId) return;
    setSyncing(true);
    setSyncError("");
    setSyncNotice("");
    try {
      const result = await syncOrderShipping(orderId);
      const notice = result?.notice || result?.Notice;
      if (notice) setSyncNotice(translateShippingSyncError(notice));
      await refetch();
    } catch (err) {
      setSyncError(err.message || "فشل المزامنة");
      await refetch();
    } finally {
      setSyncing(false);
    }
  };

  if (!orderId) return null;

  return (
    <div className={`rounded-xl border border-cyan-200 bg-cyan-50/60 ${compact ? "p-3" : "p-4"} text-right`} dir="rtl">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-bold text-cyan-900 flex items-center gap-1.5">
            <Truck size={16} />
            تتبع الشحن
          </div>
          <div className="text-xs text-cyan-700 mt-0.5">
            {data?.shippingProvider || "Fincart"}
          </div>
        </div>
        {canSync && (
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-white border border-cyan-300 text-cyan-800 hover:bg-cyan-100 disabled:opacity-50 flex items-center gap-1"
          >
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
            مزامنة
          </button>
        )}
      </div>

      {isLoading && <div className="text-sm text-cyan-700">جاري تحميل حالة الشحن...</div>}
      {error && <div className="text-sm text-red-600">فشل تحميل بيانات الشحن</div>}

      {data && (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-1 gap-1">
            <div>
              <span className="text-gray-500">رقم التتبع: </span>
              <span className="font-mono font-semibold">{data.trackingNumber || "—"}</span>
            </div>
            {data.shippingOrderId && (
              <div className="text-xs text-gray-500">
                رقم Fincart: <span className="font-mono">{data.shippingOrderId}</span>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-500">الحالة:</span>
              <span className={`text-xs font-bold rounded-full px-2 py-0.5 border ${shippingStateClass(data.shippingState)}`}>
                {shippingStateLabel(data.shippingState)}
              </span>
              {data.shippingStatus && (
                <span className="text-gray-700">
                  {data.shippingStatus}
                  {data.shippingSubStatus ? ` — ${data.shippingSubStatus}` : ""}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              آخر مزامنة: {formatDateTime(data.lastShippingSyncAt)}
            </div>
            {data.deliveredAt && (
              <div className="text-xs text-emerald-700">تم التسليم: {formatDateTime(data.deliveredAt)}</div>
            )}
            {data.returnedAt && (
              <div className="text-xs text-rose-700">مرتجع: {formatDateTime(data.returnedAt)}</div>
            )}
            {data.lastShippingSyncError && (
              <div className="text-xs text-red-600">
                خطأ المزامنة: {translateShippingSyncError(data.lastShippingSyncError)}
              </div>
            )}
            {syncNotice && <div className="text-xs text-amber-700">{syncNotice}</div>}
            {syncError && <div className="text-xs text-red-600">{syncError}</div>}
          </div>

          <div className="pt-2 mt-2 border-t border-cyan-200">
            <div className="text-xs font-bold text-cyan-900 mb-2">الجدول الزمني للتسليم</div>
            {logs.length === 0 ? (
              <div className="text-xs text-gray-500">لا توجد أحداث شحن بعد — اضغط مزامنة لجلب الحالة من Fincart</div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div key={log.id} className="relative pr-5 border-r-2 border-cyan-200 pb-2">
                    <div className="absolute right-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-cyan-600" />
                    <div className="font-semibold text-gray-800 text-sm">{log.title || "—"}</div>
                    <div className="text-xs text-gray-700">{log.description || "—"}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {formatDateTime(log.createdAt)}
                      {log.by ? ` · ${log.by}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ShippingTrackButton = ({ orderId, disabled }) => {
  const [open, setOpen] = useState(false);
  if (!orderId) return null;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="text-[10px] font-bold rounded px-1.5 py-0.5 border text-cyan-800 bg-white border-cyan-300 hover:bg-cyan-50 disabled:opacity-50"
        title="تتبع الشحن"
      >
        تتبع
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-4 relative"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute left-3 top-3 p-1 rounded-full hover:bg-gray-100"
            >
              <X size={16} />
            </button>
            <ShippingTimeline orderId={orderId} />
          </div>
        </div>
      )}
    </>
  );
};

export default ShippingTimeline;
